import { z } from "zod";

/**
 * User-facing domain for Replo Sites.
 *
 * IMPORTANT: In Cloudflare, sites are fundamentally hosted on reploshops.com.
 * The replosites.com domain redirects to reploshops.com. This constant is ONLY
 * for user-facing display purposes (URLs shown in the UI, documentation, etc.).
 *
 * For backend deployment config, DNS records, CNAME verification, and cache
 * invalidation, use reploshops.com directly.
 */
export const PUBLIC_SITES_DOMAIN = "replosites.com";

export const PUBLIC_DOMAIN_PLACEHOLDER = `your-store.${PUBLIC_SITES_DOMAIN}`;

export function getPublicDomain(subdomain: string): string {
  return `${subdomain}.${PUBLIC_SITES_DOMAIN}`;
}

export function getPublicUrl({
  subdomain,
  path,
}: {
  subdomain: string;
  path?: string;
}): string {
  return `https://${getPublicDomain(subdomain)}${path ?? ""}`;
}

// ---------------------------------------------------------------------------
// Site schemas
// ---------------------------------------------------------------------------

// The user-facing site name (DB `Site.name`), shared by the internal/public
// rename contracts and the MCP tool. Bounded to the DB column width (VarChar 255).
export const siteNameSchema = z.string().trim().min(1).max(255);

// Sandbox-provision lifecycle. `ready` means clone + install finished (bookkeeping,
// NOT "preview reachable" — readiness stays `directoryExists`); clients use this only
// to suppress duplicate provisions and surface persisted failures.
export const siteProvisioningStatusSchema = z
  .enum(["provisioning", "ready", "failed"])
  .describe("SiteProvisioningStatus");

// The canonical Replo Site, addressed by `siteId`; `SandboxSite` is this same site plus its sandbox state.
export const siteSchema = z
  .object({
    siteId: z.string(),
    subdomain: z.string(),
    name: z.string(),
    // The project's default site (at most one per project); drives entry-time redirect.
    isDefault: z.boolean(),
    createdAt: z.coerce.date(),
    // Last write time; the provisioning stale watchdog measures against it.
    // Nullish (like the provisioning fields below) so clients tolerate an older
    // API that doesn't serve it; treat missing as "not provisioning".
    updatedAt: z.coerce.date().nullish(),
    provisioningStatus: siteProvisioningStatusSchema.nullish(),
    // Sanitized, user-renderable failure summary for the `failed` state.
    provisioningError: z.string().nullish(),
    // Last SUCCESS publish time (null if never published); lets the picker fall back to the published homepage.
    lastPublishedAt: z.coerce.date().nullable(),
  })
  .describe("Site");

// A `Site` plus the absolute origin its pages are served from, which the SEO settings UI and the publish build both need.
// Nullish so clients tolerate API deploys (and rollbacks) that don't serve the field; consumers degrade to origin-less rendering.
export const siteWithCanonicalUrlSchema = siteSchema
  .extend({
    canonicalUrl: z.string().nullish(),
  })
  .describe("SiteWithCanonicalUrl");

// A `Site` plus its sandbox checkout state: `directory` is server-internal (pin-resolved, never stored, debugger-only); `directoryExists` is the gate's readiness signal.
export const sandboxSiteSchema = siteSchema
  .extend({
    directory: z.string().nullable(),
    directoryExists: z.boolean(),
  })
  .describe("SandboxSite");

// The raw persisted Site row (replo-publisher v1): identity + lifecycle timestamps, distinct from the by-id product `Site`.
export const persistedSiteSchema = z
  .object({
    id: z.uuid(),
    projectId: z.uuid(),
    subdomain: z.string(),
    name: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    deletedAt: z.coerce.date().nullish(),
  })
  .describe("PersistedSite");

// A soft-deleted (tombstoned) site, surfaced read-only in the debugger: a user-facing delete sets `deletedAt` but preserves the Artifacts repo so the row stays recoverable; unlike `SandboxSite` it carries no directory/dev-server state, only identity + lifecycle timestamps.
export const deletedSiteSchema = z
  .object({
    siteId: z.string(),
    subdomain: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    createdAt: z.coerce.date(),
    deletedAt: z.coerce.date(),
  })
  .describe("DeletedSite");

// Token-free manifest entry the sandbox pulls (GET /api/v1/sites); carries no credentials and `remoteUrl` is null when Artifacts isn't configured.
export const siteManifestEntrySchema = z
  .object({
    siteId: z.string(),
    directory: z.string(),
    remoteUrl: z.string().nullable(),
  })
  .describe("SiteManifestEntry");

export const repositoryManifestEntrySchema = z
  .object({
    repositoryId: z.string(),
    directory: z.string(),
    remoteUrl: z.string().nullable(),
  })
  .describe("RepositoryManifestEntry");

export const siteManifestSchema = z
  .object({
    sites: z.array(siteManifestEntrySchema),
    projectRepositories: z.array(repositoryManifestEntrySchema).optional(),
    // The site served on :3000 by default; null when the project has no
    // default set, in which case a single-site project still resolves.
    defaultSiteId: z.string().nullable(),
  })
  .describe("SiteManifest");
